import React, { useState, useEffect } from 'react';
import { trackEvent } from '../../utils/analytics';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AvatarPlaceholder from '../AvatarPlaceholder';
import BusinessPlaceholder from '../BusinessPlaceholder';
import { useFailedImages } from '../../hooks/useFailedImages';
import {
    Sparkles, Loader2, Lock, LockKeyhole, Phone, CheckCircle2, Heart, Send,
    ArrowRight, User, MapPin, Briefcase, MessageCircle, Undo2, Bookmark, XCircle, Compass, SlidersHorizontal,
} from 'lucide-react';
import { prefsToFilters, profileToFallbackFilters } from '../../utils/prefFilters';
import { toastInfo } from '../../utils/toastInfo';
import './ProfilesSection.css';
import './FeaturedMatches.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ProfilesSection = ({ onLoginClick }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [profiles, setProfiles] = useState([]);
    const [failedImages, markImageFailed] = useFailedImages();
    const [loading, setLoading] = useState(true);
    const [shortlistedIds, setShortlistedIds] = useState([]);
    const [wishlistIds, setWishlistIds] = useState([]);
    const [sentInterestIds, setSentInterestIds] = useState([]);
    const [revealedNumberIds, setRevealedNumberIds] = useState([]);
    const [numberRequestStatuses, setNumberRequestStatuses] = useState({});
    const [numberActionLoadingIds, setNumberActionLoadingIds] = useState([]);
    const [vendorServices, setVendorServices] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(true);

    useEffect(() => {
        if (user?.role === 'service') fetchVendorServices();
    }, [user]);

    const fetchVendorServices = async () => {
        try {
            const res = await axios.get(`${API}/api/services`);
            const others = (res.data.services || []).filter(s => String(s.vendor?._id || s.vendor) !== String(user.id));
            setVendorServices(others.slice(0, 6));
        } catch (err) {
            setVendorServices([]);
        } finally {
            setLoadingVendors(false);
        }
    };

    useEffect(() => { if (user?.role !== 'service') fetchProfiles(); else setLoading(false); }, [user]);
    useEffect(() => { if (user) { fetchShortlistedIds(); fetchWishlistIds(); fetchSentInterestIds(); } }, [user]);

    // A member starts with the same filter as the Browse page: the saved partner preferences, or (when none are saved)
    // religion, city, age, caste, sub-caste and marital status taken from their own profile.
    // matchNote: 'prefs' | 'auto' | 'similar' = which of those is on screen, '' = everyone (or nothing to filter by).
    const [matchNote, setMatchNote] = useState('');
    const [startFilters, setStartFilters] = useState({}); // what "Show my matches" goes back to
    const [prefsSaved, setPrefsSaved] = useState(false);
    const [showingAll, setShowingAll] = useState(false);

    const authHeaders = () => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };
    const oppositeGender = user?.gender === 'Female' ? 'Male' : user?.gender === 'Male' ? 'Female' : '';

    // the profiles that fit `filters` (opposite gender only); the total is returned so the caller can widen the search
    const searchProfiles = async (filters) => {
        const params = {
            ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')),
            ...(oppositeGender ? { gender: oppositeGender } : {}),
            page: 1, limit: 8,
        };
        const res = await axios.get(`${API}/api/profiles`, { headers: authHeaders(), params });
        return { list: res.data.profiles || [], total: res.data.total || 0 };
    };

    const fetchProfiles = async () => {
        try {
            if (user?.role === 'member') {
                let start = {};
                let saved = false;
                try {
                    const mine = (await axios.get(`${API}/api/profiles/my`, { headers: authHeaders() })).data.profile;
                    saved = Boolean(mine?.prefCompleted);
                    start = saved ? prefsToFilters(mine) : profileToFallbackFilters(mine);
                } catch { /* no profile yet: show the suggestions below */ }
                if (Object.keys(start).length) {
                    setPrefsSaved(saved);
                    setStartFilters(start);
                    setShowingAll(false);
                    let found = await searchProfiles(start);
                    let note = saved ? 'prefs' : 'auto';
                    if (found.total === 0) {
                        // nobody fits every field: widen the search step by step (most specific first), like the Browse page
                        const widened = { ...start };
                        for (const key of ['subCaste', 'caste', 'dosham', 'nakshatra', 'rasi', 'maritalStatus', 'minAge', 'maxAge', 'district', 'state', 'city', 'religion']) {
                            if (!widened[key]) continue;
                            widened[key] = '';
                            // eslint-disable-next-line no-await-in-loop
                            found = await searchProfiles(widened);
                            note = 'similar';
                            if (found.total > 0) break;
                        }
                    }
                    setProfiles(found.list);
                    setMatchNote(note);
                    return;
                }
            }
            // everyone else (and a member with nothing to filter by): /suggested already keeps to opposite-gender profiles
            const res = await axios.get(`${API}/api/profiles/suggested`, { headers: authHeaders() });
            setProfiles((res.data.profiles || []).slice(0, 8));
            setMatchNote('');
        } catch (err) {
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    };

    // "Show all profiles": drop the filter and show everyone of the opposite gender
    const showAllProfiles = async () => {
        try {
            const found = await searchProfiles({});
            setProfiles(found.list);
            setShowingAll(true);
            setMatchNote('');
        } catch { toast.error('Could not load the profiles. Please try again.'); }
    };

    // back to the member's matches after "Show all profiles"
    const showMyMatches = async () => {
        setLoading(true);
        await fetchProfiles();
    };

    const fetchShortlistedIds = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API}/api/shortlist/my`, { headers: { Authorization: `Bearer ${token}` } });
            setShortlistedIds((res.data.profiles || []).map(p => p._id));
        } catch (err) { }
    };

    const fetchWishlistIds = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API}/api/wishlist/my`, { headers: { Authorization: `Bearer ${token}` } });
            setWishlistIds((res.data.profiles || []).map(p => p._id));
        } catch (err) { }
    };

    const fetchSentInterestIds = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API}/api/interests/sent`, { headers: { Authorization: `Bearer ${token}` } });
            setSentInterestIds((res.data.interests || []).map(i => i.profile?._id).filter(Boolean));
        } catch (err) { }
    };

    const handleSendInterest = async (p) => {
        if (!user) { onLoginClick(); return; }
        const token = localStorage.getItem('token');

        // Already sent — clicking again withdraws it
        if (sentInterestIds.includes(p._id)) {
            try {
                await axios.delete(`${API}/api/interests/${p._id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSentInterestIds(prev => prev.filter(id => id !== p._id));
                toast('Interest withdrawn', { icon: <Undo2 size={16} color="var(--primary)" /> });
            } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to withdraw interest');
            }
            return;
        }

        try {
            await axios.post(`${API}/api/interests/send`,
                { profileId: p._id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSentInterestIds(prev => [...prev, p._id]);
            trackEvent('send_interest', { source: 'home' });
            toast.success(`Interest sent to ${getName(p)}!`);
        } catch (err) {
            if (err.response?.data?.alreadySent) {
                setSentInterestIds(prev => [...prev, p._id]);
                toast('Interest already sent!');
            } else {
                toast.error(err.response?.data?.message || 'Failed to send interest');
            }
        }
    };

    const handleShortlist = async (p) => {
        if (!user) { onLoginClick(); return; }
        const isShortlisted = shortlistedIds.includes(p._id);
        try {
            const token = localStorage.getItem('token');
            if (isShortlisted) {
                await axios.delete(`${API}/api/shortlist/remove/${p._id}`, { headers: { Authorization: `Bearer ${token}` } });
                setShortlistedIds(prev => prev.filter(id => id !== p._id));
                toast.success('Removed from shortlist!');
            } else {
                await axios.post(`${API}/api/shortlist/add`, { profileId: p._id }, { headers: { Authorization: `Bearer ${token}` } });
                setShortlistedIds(prev => [...prev, p._id]);
                toast.success('Shortlisted!');
            }
        } catch (err) { toast.error(err.response?.data?.message || 'Failed!'); }
    };

    const handleWishlist = async (p) => {
        if (!user) { onLoginClick(); return; }
        const isWishlisted = wishlistIds.includes(p._id);
        try {
            const token = localStorage.getItem('token');
            if (isWishlisted) {
                await axios.delete(`${API}/api/wishlist/remove/${p._id}`, { headers: { Authorization: `Bearer ${token}` } });
                setWishlistIds(prev => prev.filter(id => id !== p._id));
                toast.success('Removed from wishlist!');
            } else {
                await axios.post(`${API}/api/wishlist/add`, { profileId: p._id }, { headers: { Authorization: `Bearer ${token}` } });
                setWishlistIds(prev => [...prev, p._id]);
                toast.success('Added to wishlist!');
            }
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to update wishlist'); }
    };

    const handleViewNumber = async (p) => {
        if (!user) { onLoginClick(); return; }
        if (!user?.isPremium) { toastInfo('Upgrade to a Premium plan to view phone numbers.'); return; }
        if (numberActionLoadingIds.includes(p._id)) return; // already checking
        // the reason is told when the button is tapped, so the button itself never looks switched off
        if (numberRequestStatuses[p._id] === 'pending') { toastInfo('Your number request is awaiting approval.'); return; }
        if (numberRequestStatuses[p._id] === 'rejected') { toast.error('Your number request was declined.'); return; }
        if (revealedNumberIds.includes(p._id)) {
            setRevealedNumberIds(prev => prev.filter(id => id !== p._id));
            return;
        }

        const revealNumber = async (mobile) => {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/api/number-views/log`, { profileId: p._id }, {
                headers: { Authorization: `Bearer ${token}` },
            }).catch(() => { /* the API still protects access */ });
            setRevealedNumberIds(prev => prev.includes(p._id) ? prev : [...prev, p._id]);
            setNumberRequestStatuses(prev => ({ ...prev, [p._id]: 'approved' }));
            setProfiles(prev => prev.map(profile => profile._id === p._id
                ? { ...profile, user: { ...profile.user, mobile } }
                : profile));
        };

        if (p.user?.mobile) {
            await revealNumber(p.user.mobile);
            return;
        }

        if (!p.numberProtected) {
            toast('Phone number has not been provided for this profile.', { icon: 'ℹ️' });
            return;
        }

        setNumberActionLoadingIds(prev => [...prev, p._id]);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const statusResponse = await axios.get(`${API}/api/privacy/check/${p._id}`, { headers });
            const existingRequest = statusResponse.data.request;

            if (existingRequest?.status === 'approved') {
                const profileResponse = await axios.get(`${API}/api/profiles/${p._id}`, { headers });
                const approvedMobile = profileResponse.data.profile?.user?.mobile;
                if (approvedMobile) {
                    await revealNumber(approvedMobile);
                    return;
                }
            }

            if (existingRequest) {
                setNumberRequestStatuses(prev => ({ ...prev, [p._id]: existingRequest.status }));
                toast(existingRequest.status === 'pending' ? 'Number request is awaiting approval' : 'Number request was declined');
                return;
            }

            await axios.post(`${API}/api/privacy/request`, { profileId: p._id }, { headers });
            setNumberRequestStatuses(prev => ({ ...prev, [p._id]: 'pending' }));
            toast.success('Phone number request sent!');
        } catch (err) {
            const status = err.response?.data?.status;
            if (status) setNumberRequestStatuses(prev => ({ ...prev, [p._id]: status }));
            toast.error(err.response?.data?.message || 'Could not request the phone number');
        } finally {
            setNumberActionLoadingIds(prev => prev.filter(id => id !== p._id));
        }
    };

    const handleWhatsApp = (p) => {
        if (!user) { onLoginClick(); return; }
        if (!user?.isPremium) { toastInfo('Upgrade to Premium!'); return; }
        const mobile = p.user?.mobile;
        if (mobile) {
            trackEvent('contact_click', { method: 'whatsapp', source: 'home' });
            window.open(`https://wa.me/91${mobile}`, '_blank');
        }
    };

    const handleChat = (p) => {
        if (!user) { onLoginClick(); return; }
        if (!user.isPremium) { toastInfo('Upgrade to Premium to message members!'); return; }
        const profileUserId = p.user?._id || p.user;
        if (profileUserId) navigate(`/messages?with=${profileUserId}`);
    };

    const getName = (p) => {
        if (!p) return 'Unknown';
        const profileFor = p.profileFor || p.user?.profileFor;
        if (profileFor && profileFor !== 'Myself') {
            return p.profileName || p.user?.profileName || p.name || p.user?.name || 'Unknown';
        }
        return p.name || p.user?.profileName || p.user?.name || 'Unknown';
    };
    const getAge = (dob) => {
        if (!dob) return null;
        const age = Math.floor((new Date() - new Date(dob)) / (365.25 * 24 * 60 * 60 * 1000));
        return age > 0 ? age : null;
    };

    // ✅ Check if user is unverified member
    const isUnverified = user && user.role === 'member' && !user.isVerified;

    // ✅ Service accounts see other vendors here instead of member profiles
    // (member profiles are private to matrimony members) — with its own
    // heading, since "Find Your Perfect Match" doesn't fit a vendor.
    if (user && user.role === 'service') return (
        <section className="home-profiles fm-section">
            <div className="fm-inner">
                <div className="fm-header">
                    <div className="fm-heading">
                        <p className="fm-eyebrow">Fellow Vendors</p>
                        <h2>Discover Other <em>Wedding Vendors</em></h2>
                        <p className="fm-sub">See who else is listed on Gettimelam Matrimony.</p>
                    </div>
                    <div className="fm-controls">
                        <button type="button" className="fm-viewall" onClick={() => navigate('/services')}>
                            View All Wedding Services <ArrowRight size={14} />
                        </button>
                    </div>
                </div>

                {loadingVendors ? (
                    <div className="fm-state">
                        <Loader2 size={38} className="spin" style={{ color: 'var(--c-7a1230, #7a1230)' }} />
                        <p>Loading vendors...</p>
                    </div>
                ) : vendorServices.length === 0 ? (
                    <div className="fm-state fm-state--empty">
                        <span className="fm-state__icon"><BusinessPlaceholder variant="badge" size={56} /></span>
                        <p>No other vendors listed yet — check back soon.</p>
                        <button type="button" className="fm-cta" onClick={() => navigate('/services')}>
                            Explore Wedding Services <ArrowRight size={15} />
                        </button>
                    </div>
                ) : (
                    <div className="fm-track fm-track--wrap">
                        {vendorServices.map(s => {
                            const registeredBrand = s.vendor?.businessName;
                            const titleParts = String(s.businessName || '').split(/\s+-\s+/);
                            const brandName = registeredBrand || titleParts[0] || 'Wedding Professional';
                            const serviceName = titleParts.length > 1
                                ? titleParts.slice(1).join(' - ')
                                : (s.category || 'Wedding Services');
                            const brandInitials = brandName.split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]).join('').toUpperCase();
                            const coverImage = s.photos?.[0] || s.vendor?.logo;
                            const location = [...new Set([s.city, s.district].filter(Boolean))].join(', ');

                            return (
                                <article key={s._id} className="fm-card fm-card--vendor">
                                    <div className="fm-photo" onClick={() => navigate(`/services/${s._id}`)}>
                                        {coverImage && !failedImages.has(coverImage) ? (
                                            <img src={`${API}${coverImage}`} alt={`${brandName} business`} onError={() => markImageFailed(coverImage)} />
                                        ) : (
                                            <div className="fm-vendor-placeholder" aria-label={`${brandName} brand`}>
                                                <span>{brandInitials || <BusinessPlaceholder variant="badge" size={46} />}</span>
                                                <small>Wedding professional</small>
                                            </div>
                                        )}
                                        <div className="fm-vendor-photo-shade" />
                                        {s.category && <span className="fm-badge">{s.category}</span>}
                                        {s.isVerified && <span className="fm-vendor-verified"><CheckCircle2 size={12} /> Verified</span>}
                                    </div>
                                    <div className="fm-body">
                                        <div className="fm-vendor-kicker">
                                            <span>Featured business</span>
                                            {s.vendor?.providerId && <strong>{s.vendor.providerId}</strong>}
                                        </div>
                                        <h3 onClick={() => navigate(`/services/${s._id}`)}>{brandName}</h3>
                                        <div className="fm-vendor-service"><Briefcase size={13} /> {serviceName}</div>
                                        <div className="fm-vendor-meta">
                                            <span><User size={13} /><small>Managed by</small><strong>{s.vendor?.ownerName || s.ownerName || 'Owner'}</strong></span>
                                            {location && <span><MapPin size={13} /><small>Serving</small><strong>{location}</strong></span>}
                                        </div>
                                        <div className="fm-vendor-price">
                                            <span>Service investment</span>
                                            <strong>{s.priceMin && s.priceMax ? `₹${s.priceMin.toLocaleString()} – ₹${s.priceMax.toLocaleString()}` : 'Request a quote'}</strong>
                                        </div>
                                        <div className="fm-actions">
                                            <a className="fm-view fm-view--solid" href={s.mobile ? `tel:${s.mobile}` : undefined}
                                                onClick={e => { e.stopPropagation(); if (!s.mobile) e.preventDefault(); }}>
                                                <Phone size={13} /> Call vendor
                                            </a>
                                            <button type="button" className="fm-view" onClick={() => navigate(`/services/${s._id}`)}>
                                                View profile <ArrowRight size={13} />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );

    if (loading) return (
        <section className="home-profiles home-section profiles-showcase profiles-showcase--loading">
            <div className="profiles-showcase__loader">
                <Loader2 size={38} style={{ marginBottom: '12px', color: 'var(--primary)', animation: 'spin 1s linear infinite' }} />
                <p style={{ color: 'var(--c-7a6055, #7a6055)' }}>Loading profiles...</p>
            </div>
        </section>
    );

    return (
        <section className="home-profiles fm-section">
            <div className="fm-inner">
                <div className="fm-header">
                    <div className="fm-heading">
                        <p className="fm-eyebrow">Featured Matches</p>
                        <h2>Meet Someone Who Shares <em>What Matters</em></h2>
                    </div>
                    <div className="fm-controls">
                        <button type="button" className="fm-viewall" onClick={() => navigate('/browse')}>
                            View All Profiles <ArrowRight size={14} />
                        </button>
                    </div>
                </div>

                {/* Which profiles are shown first, with a way to see everyone and to change the preferences */}
                {user?.role === 'member' && matchNote && (
                    <div className="fm-pref-banner">
                        {matchNote === 'similar' ? <Heart size={17} /> : <Compass size={17} />}
                        <span>
                            {matchNote === 'prefs' && <>Showing profiles that match <strong>your partner preferences</strong>.</>}
                            {matchNote === 'auto' && <>Showing automatic matches based on your <strong>religion, city, age, caste, sub-caste and marital status</strong>.</>}
                            {matchNote === 'similar' && <>No profile fits every match field, so here are <strong>similar profiles</strong>.</>}
                        </span>
                        <button type="button" onClick={showAllProfiles}>Show all profiles</button>
                        <button type="button" className="is-ghost" onClick={() => navigate('/dashboard?tab=profile')}>
                            <SlidersHorizontal size={13} /> {prefsSaved ? 'Edit preferences' : 'Set preferences'}
                        </button>
                    </div>
                )}
                {user?.role === 'member' && showingAll && Object.keys(startFilters).length > 0 && (
                    <div className="fm-pref-banner">
                        <Compass size={17} />
                        <span>Showing <strong>all profiles</strong>.</span>
                        <button type="button" onClick={showMyMatches}>Show my matches</button>
                        <button type="button" className="is-ghost" onClick={() => navigate('/dashboard?tab=profile')}>
                            <SlidersHorizontal size={13} /> {prefsSaved ? 'Edit preferences' : 'Set preferences'}
                        </button>
                    </div>
                )}

                {/* Verification banner */}
                {isUnverified && (
                    <div className="profiles-showcase__verify-banner">
                        <div className="profiles-showcase__verify-copy">
                            <Lock size={28} color="#fff" style={{ marginRight: '14px', flexShrink: 0 }} />
                            <div>
                                <div style={styles.verifyBannerTitle}>Account Pending Verification</div>
                                <div style={styles.verifyBannerDesc}>
                                    Our team will call you within 24 hours to verify your account.
                                    Profile cards will be unlocked after verification.
                                </div>
                            </div>
                        </div>
                        <a href="/contact" className="profiles-showcase__support-link">
                            <MessageCircle size={14} />
                            Contact Support
                        </a>
                    </div>
                )}

                <div className="fm-track fm-track--wrap">
                    {profiles.map((p, i) => {
                        const isShortlisted = shortlistedIds.includes(p._id);
                        const isWishlisted = wishlistIds.includes(p._id);
                        const isSent = sentInterestIds.includes(p._id);
                        const age = getAge(p.dateOfBirth);
                        const isNumberRevealed = revealedNumberIds.includes(p._id);
                        const numberRequestStatus = numberRequestStatuses[p._id];
                        const isNumberActionLoading = numberActionLoadingIds.includes(p._id);
                        const open = () => {
                            if (isUnverified) {
                                toast('Account pending verification. Full profile will unlock once verified.', {
                                    icon: <LockKeyhole size={18} strokeWidth={2.2} color="var(--primary)" aria-hidden="true" />,
                                });
                                return;
                            }
                            navigate(`/profile/${p._id}`);
                        };

                        return (
                            <article key={p._id || i} className="fm-card">
                                <div className="fm-photo">
                                    {p.photo && !failedImages.has(p.photo) ? (
                                        <img
                                            src={`${API}${p.photo}`}
                                            alt={getName(p)}
                                            onError={() => markImageFailed(p.photo)}
                                            onClick={open}
                                            style={isUnverified ? { filter: 'blur(10px)', transform: 'scale(1.12)', transition: 'filter 0.3s ease' } : {}}
                                        />
                                    ) : (
                                        <span
                                            className="fm-avatar"
                                            onClick={open}
                                            style={isUnverified ? { filter: 'blur(8px)', transform: 'scale(1.08)', display: 'inline-block' } : {}}
                                        >
                                            <AvatarPlaceholder gender={p.gender} size={150} variant="card" />
                                        </span>
                                    )}
                                    {isUnverified && (
                                        <div className="fm-lock" style={{ pointerEvents: 'none' }}>
                                            <Lock size={20} color="#fff" />
                                        </div>
                                    )}
                                    <button type="button" className={`fm-heart fm-heart--wishlist ${isWishlisted ? 'is-active' : ''}`} onClick={() => handleWishlist(p)} title={isWishlisted ? 'Remove from wishlist' : 'Save to wishlist'} aria-label={isWishlisted ? 'Remove from wishlist' : 'Save to wishlist'}>
                                        <Bookmark size={17} fill={isWishlisted ? 'currentColor' : 'none'} />
                                        <Heart className="fm-wishlist-heart" size={8} fill="currentColor" />
                                    </button>
                                    <button type="button" className={`fm-heart ${isShortlisted ? 'is-active' : ''}`} onClick={() => handleShortlist(p)} title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'} aria-label={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}>
                                        <Heart size={14} fill={isShortlisted ? '#d62839' : 'none'} color={isShortlisted ? '#d62839' : 'var(--c-7a1230, #7a1230)'} />
                                    </button>
                                </div>
                                <div className="fm-body">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                                        <h3 onClick={open} title={getName(p)} style={{ margin: 0 }}>{getName(p)}</h3>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary)', background: 'var(--primary-pale, rgba(122,23,51,0.08))', padding: '2px 7px', borderRadius: '6px' }}>{p.memberId || 'Pending assignment'}</span>
                                    </div>
                                    {p.isDeceased && (
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: '600', marginBottom: '6px' }}>
                                            🕊️ In Remembrance
                                        </div>
                                    )}
                                    <ul className="fm-details">
                                        {age && <li><User size={14} /><span><small>Age &amp; height</small><strong>{age} Years{p.height ? ` · ${p.height}` : ''}</strong></span></li>}
                                        {(p.city || p.state) && <li><MapPin size={14} /><span><small>Location</small><strong>{[p.city, p.state || 'Tamil Nadu'].filter(Boolean).join(', ')}</strong></span></li>}
                                        {(p.occupation || p.education) && <li><Briefcase size={14} /><span><small>Profession</small><strong>{p.occupation || p.education}</strong></span></li>}
                                    </ul>
                                    <div className="fm-actions">
                                        <button type="button" className="fm-view" onClick={open}>View Profile <ArrowRight size={14} /></button>
                                        <button type="button" className={`fm-interest ${isSent ? 'is-sent' : ''}`} onClick={() => handleSendInterest(p)}
                                            title={isSent ? 'Interest sent - tap to cancel' : 'Send interest'} aria-label={isSent ? 'Cancel interest' : 'Send interest'}>
                                            {isSent ? <CheckCircle2 size={15} /> : <Send size={15} />}
                                        </button>
                                    </div>
                                    <div className="fm-premium-actions" aria-label="Premium contact actions">
                                        <button
                                            type="button"
                                            className={`fm-premium-action ${user?.isPremium ? '' : 'is-locked'}`}
                                            onClick={() => handleChat(p)}
                                            title={user?.isPremium ? 'Send message' : 'Premium plan required'}
                                        >
                                            {user?.isPremium ? <MessageCircle size={14} /> : <Lock size={13} />}
                                            Send Message
                                        </button>
                                        <button
                                            type="button"
                                            className={`fm-premium-action ${isNumberRevealed ? 'is-revealed' : ''} ${!user?.isPremium || ['pending', 'rejected'].includes(numberRequestStatus) ? 'is-locked' : ''}`}
                                            onClick={() => handleViewNumber(p)}
                                            title={!user?.isPremium ? 'Premium plan required' : isNumberRevealed ? 'Hide phone number' : p.numberProtected ? 'Request phone number access' : 'View phone number'}
                                        >
                                            {!user?.isPremium ? <Lock size={13} /> : isNumberActionLoading ? <Loader2 size={14} className="spin" /> : numberRequestStatus === 'pending' ? <CheckCircle2 size={14} /> : numberRequestStatus === 'rejected' ? <XCircle size={14} /> : <Phone size={14} />}
                                            {isNumberActionLoading
                                                ? 'Checking...'
                                                : isNumberRevealed && p.user?.mobile
                                                    ? `+91 ${p.user.mobile}`
                                                    : numberRequestStatus === 'pending'
                                                        ? 'Request Sent'
                                                        : numberRequestStatus === 'rejected'
                                                            ? 'Request Declined'
                                                            : 'View Number'}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}

                    {profiles.length === 0 && (
                        <div className="profiles-showcase__empty">
                            <Heart size={48} style={{ marginBottom: '12px', color: 'var(--primary)' }} />
                            <p style={{ fontWeight: 700, fontSize: '17px', marginBottom: '6px' }}>Your matches are coming soon</p>
                            <p style={{ maxWidth: '460px', margin: '0 auto 16px' }}>
                                Every profile here is verified by our team before it is shown, and new verified profiles are joining every day. Please check back soon.
                            </p>
                            <button type="button" className="fm-viewall" onClick={() => navigate(user ? '/services' : '/register')}>
                                {user ? 'Explore Wedding Services' : 'Create Your Free Profile'} <ArrowRight size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const styles = {
    section: { padding: 'clamp(76px, 8vw, 118px) 24px', background: 'var(--bg)' },
    inner: { maxWidth: '1200px', margin: '0 auto' },
    header: { textAlign: 'center', marginBottom: '48px' },
    label: { fontSize: '11px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: '10px' },
    title: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 'clamp(39px, 4.2vw, 58px)', fontWeight: '600', color: 'var(--dark)', marginBottom: '12px' },
    desc: { fontSize: '15px', color: 'var(--text-muted)' },

    // ✅ Verification Banner
    verifyBanner: { background: 'linear-gradient(135deg, var(--primary), var(--primary-light))', borderRadius: '16px', padding: '20px 24px', marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' },
    verifyBannerLeft: { display: 'flex', alignItems: 'center', flex: 1 },
    verifyBannerTitle: { fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '4px' },
    verifyBannerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 },
    verifyBannerBtn: { padding: '10px 20px', background: 'var(--border)', color: 'var(--dark)', borderRadius: '8px', fontSize: '13px', fontWeight: '700', textDecoration: 'none', whiteSpace: 'nowrap' },

    // ✅ Blur overlay
    // Covers the whole card so unverified users can't click through to
    // shortlist/interest actions underneath — the transparent background
    // lets the blurred photo show through instead of hiding it entirely.
    lockBadge: { position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' },
    lockBadgeIcon: { width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' },

    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 240px))', gap: '18px', justifyContent: 'center' },
    card: {
        background: 'linear-gradient(160deg, var(--primary-dark) 0%, var(--nav-dark) 100%)',
        borderRadius: '18px', overflow: 'hidden',
        boxShadow: '0 14px 38px color-mix(in srgb, var(--primary) 18%, transparent)',
        border: '1px solid color-mix(in srgb, var(--border) 35%, transparent)',
        width: '100%',
    },
    photo: { height: '290px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer', overflow: 'hidden' },
    vendorCardWrap: { background: 'linear-gradient(160deg, var(--c-14100f, #14100f), #0A0808)', borderRadius: '18px', overflow: 'hidden', position: 'relative', border: '1px solid color-mix(in srgb, var(--c-f5be17, #f5be17) 25%, transparent)', boxShadow: '0 0 0 1px rgba(216,73,46,0.1), 0 16px 40px rgba(0,0,0,0.5)' },
    vendorPhoto: { height: '190px', background: 'linear-gradient(140deg, var(--c-b8267a, #b8267a) 0%, #6B1B6B 45%, #2E1065 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer', overflow: 'hidden', borderBottom: '1px solid color-mix(in srgb, var(--c-f5be17, #f5be17) 25%, transparent)' },
    vendorPhotoGlow: { position: 'absolute', width: '180px', height: '180px', background: 'radial-gradient(circle, color-mix(in srgb, var(--c-f5be17, #f5be17) 35%, transparent), transparent 70%)', filter: 'blur(10px)', pointerEvents: 'none' },
    vendorPhotoSheen: { position: 'absolute', inset: 0, background: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.18), transparent 45%)', pointerEvents: 'none' },
    vendorPhotoLine: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, var(--border), transparent)' },
    vendorCategoryBadge: { position: 'absolute', top: '12px', left: '12px', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--c-ffe9c9, #ffe9c9)', fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', padding: '5px 12px', borderRadius: '4px' },
    vendorBody: { padding: '20px' },
    vendorName: { fontFamily: "'DM Sans', sans-serif", fontSize: '17px', fontWeight: '700', color: '#fff', marginBottom: '10px', cursor: 'pointer', letterSpacing: '0.3px' },
    vendorMetaRow: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    vendorMetaIcon: { color: 'var(--c-d8497a, #d8497a)' },
    vendorPrice: { fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: '700', color: 'var(--c-f5d98b, #f5d98b)', margin: '10px 0 16px' },
    vendorActions: { display: 'flex', gap: '8px' },
    vendorBtnPrimary: { flex: 1, textAlign: 'center', padding: '11px 0', background: 'linear-gradient(135deg, var(--c-b8267a, #b8267a), #6B1B6B)', color: '#fff', borderRadius: '8px', fontSize: '11.5px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none', display: 'block', boxShadow: '0 4px 16px color-mix(in srgb, var(--c-b8267a, #b8267a) 35%, transparent)' },
    vendorBtnOutline: { flex: 1, padding: '11px 0', background: 'transparent', border: '1px solid color-mix(in srgb, var(--c-f5be17, #f5be17) 40%, transparent)', color: 'var(--c-f5d98b, #f5d98b)', borderRadius: '8px', fontSize: '11.5px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer' },
    avatar: { fontSize: '56px', cursor: 'pointer' },
    photoGradient: { position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 35%, transparent 55%, rgba(0,0,0,0.85) 100%)', pointerEvents: 'none' },
    verified: { position: 'absolute', top: '10px', left: '10px', background: 'rgba(30,107,60,0.92)', color: '#fff', fontSize: '10.5px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px' },
    heartBtn: { position: 'absolute', top: '9px', right: '9px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' },
    nameOverlay: { position: 'absolute', bottom: '10px', left: '14px', right: '14px', cursor: 'pointer' },
    overlayName: { fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '22px', fontWeight: '700', color: '#fff', textShadow: '0 2px 6px rgba(0,0,0,0.6)', marginBottom: '4px' },
    overlayMeta: { fontSize: '11px', color: 'var(--c-e4d0c8, #e4d0c8)', lineHeight: 1.4, textShadow: '0 1px 4px rgba(0,0,0,0.5)' },
    body3: { padding: '10px 12px 12px' },
    btnInterest: { width: '100%', padding: '10px', border: 'none', borderRadius: '9px', color: '#fff', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', marginBottom: '7px', transition: 'box-shadow 0.2s ease' },
    btnViewProfile: { width: '100%', padding: '9px', borderRadius: '9px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid color-mix(in srgb, var(--border) 65%, transparent)', color: 'var(--border)', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', transition: 'box-shadow 0.2s ease' },
    info: { padding: '14px 16px' },
    nameRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' },
    name: { fontFamily: "'Playfair Display', serif", fontSize: '17px', fontWeight: '600', color: 'var(--primary-pale)', cursor: 'pointer', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    contactIcons: { display: 'flex', gap: '6px' },
    phoneIcon: { width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #4CAF50', background: '#fff', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    waIcon: { width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #25D366', background: '#25D366', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    meta: { fontSize: '12.5px', color: 'var(--c-c9a876, #c9a876)', marginBottom: '2px', lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    tags: { display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '8px 0' },
    tag: { fontSize: '10.5px', fontWeight: '600', padding: '3px 9px', borderRadius: '20px', background: 'color-mix(in srgb, var(--c-f5d98b, #f5d98b) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--c-f5d98b, #f5d98b) 35%, transparent)', color: 'var(--c-f5d98b, #f5d98b)', whiteSpace: 'nowrap' },
    actions: { display: 'flex', gap: '6px', marginTop: '8px', marginBottom: '7px' },
    btnPrimary: { flex: 1, padding: '9px', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
    btnOutline: { flex: 1, padding: '9px', background: 'rgba(255,255,255,0.06)', color: 'var(--c-f5d98b, #f5d98b)', border: '1.5px solid color-mix(in srgb, var(--c-f5d98b, #f5d98b) 50%, transparent)', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
    btnLike: { padding: '9px 12px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' },
    chatBtn: { width: '100%', padding: '9px', background: 'linear-gradient(135deg, #1565C0, #1976D2)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    center: { textAlign: 'center', marginTop: '36px' },
    viewAll: { padding: '12px 32px', background: 'transparent', border: '1.5px solid #8B1A1A', color: '#8B1A1A', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: 'pointer' },
};

export default ProfilesSection;
